/**
 * R1C1 Formula Builders for Option Chains
 * Generates Excel formulas using R1C1 notation for location independence
 */

import { needsBracketAccess } from "../../taskpane/options/formulas";

/**
 * Build entity property reference using R1C1 notation
 * @param entityRef - R1C1 reference to entity cell (e.g., "RC1" for same row, column 1)
 * @param propertyName - Property name (e.g., "IV", "Open Interest")
 * @returns R1C1 property reference (e.g., "RC1.IV" or "RC1.[Open Interest]")
 */
export function buildEntityPropertyRefR1C1(entityRef: string, propertyName: string): string {
  if (needsBracketAccess(propertyName)) {
    // Bracket access for fields with spaces/special chars: RC1.[Open Interest]
    return entityRef + ".[" + propertyName + "]";
  } else {
    // Dot access for simple identifiers: RC1.IV
    return entityRef + "." + propertyName;
  }
}

/**
 * Build numeric formula gated on entity error (ISERROR).
 * When entity cell is #N/A or #CONNECT!, property columns stay clean (0 or "").
 * @param entityRef - R1C1 reference to entity cell (e.g. "RC[-1]")
 * @param propRef - Property reference (e.g. "RC[-1].IV")
 * @param defaultMode - "zero" for count fields (missing → 0), "blank" for prices/greeks (missing → blank)
 * @returns Excel formula string
 */
export function buildNumericFormula(entityRef: string, propRef: string, defaultMode: "zero" | "blank"): string {
  if (defaultMode === "zero") {
    // RC1 error → 0; missing value → 0; true 0 preserved; no #FIELD!, no #VALUE!
    return "=IF(ISERROR(" + entityRef + "),0,IFERROR(IF(" + propRef + '="","",VALUE(' + propRef + ")),0))";
  } else {
    // RC1 error → blank; missing value → blank; true 0 preserved
    return "=IF(ISERROR(" + entityRef + '),"",IF(' + propRef + '="","",IFERROR(VALUE(' + propRef + '),"")))';
  }
}

/**
 * Build text formula gated on entity error (ISERROR).
 * @param entityRef - R1C1 reference to entity cell
 * @param propRef - Property reference (e.g. "RC[-1].Underlying")
 * @returns Excel formula string
 */
export function buildTextFormula(entityRef: string, propRef: string): string {
  return "=IF(ISERROR(" + entityRef + "),\"\",IFERROR(" + propRef + ',""))';
}

/**
 * Build datetime formula gated on entity error (ISERROR).
 * No VALUE() for UpdatedAt (already numeric serial).
 * @param entityRef - R1C1 reference to entity cell
 * @param propRef - Property reference (e.g. "RC[-1].UpdatedAt")
 * @returns Excel formula string
 */
export function buildDateTimeFormula(entityRef: string, propRef: string): string {
  return "=IF(ISERROR(" + entityRef + "),\"\",IFERROR(" + propRef + ',""))';
}
